/**
 * TemplateEditView component for creating/editing a notification template
 * Split-pane layout: Edit form on left, live preview on right
 * Includes pre-made HTML email templates with customizable colors
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  Mail, MessageSquare, Code,
  X, AlertCircle, Eye, Save, Smartphone, Monitor,
  Layout, Wand2
} from 'lucide-react';
import { getTemplateVariables, TemplateVariables } from '../../utils/notificationsBroadcastApi';
import {
  NotificationTemplate,
  NotificationChannel,
  TemplateCreateRequest,
  getTemplates,
  createTemplate,
  updateTemplate,
} from '../../utils/notificationsBroadcastApi';
import { useAuth } from '../../contexts/AuthContext';
import { API_BASE_URL } from '../../config/api';
import { EmailBuilder } from './EmailBuilder';
import { EmailDocument } from './builder/types';

// =============================================================================
// Component
// =============================================================================

interface TemplateEditViewProps {
  templateId?: string; // undefined for new template
  onBack: () => void;
  onSave?: () => void;
  initialChannel?: NotificationChannel; // Pre-select channel for new templates
  onTemplateNameChange?: (name: string | null) => void; // Callback when template name is loaded/changed
}

export const TemplateEditView: React.FC<TemplateEditViewProps> = ({ templateId: initialTemplateId, onBack, onSave, initialChannel, onTemplateNameChange }) => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [template, setTemplate] = useState<NotificationTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [practiceName, setPracticeName] = useState<string>('Your Practice');
  const [practiceEmail, setPracticeEmail] = useState<string>('noreply@yourpractice.com');
  const [practiceCity, setPracticeCity] = useState<string>('');
  const [practiceState, setPracticeState] = useState<string>('');
  const [practiceSlug, setPracticeSlug] = useState<string>('');
  const [practiceOnlineBookingEnabled, setPracticeOnlineBookingEnabled] = useState<boolean>(false);
  
  // Track template ID - may change from undefined to actual ID after first save
  const [currentTemplateId, setCurrentTemplateId] = useState<string | undefined>(initialTemplateId);

  // Form state
  const [formName, setFormName] = useState('');
  const [formChannel, setFormChannel] = useState<NotificationChannel>(initialChannel || 'email_html');
  const [formSubject, setFormSubject] = useState('');
  const [formBody, setFormBody] = useState('');

  // Variable toolbar state
  const [variables, setVariables] = useState<TemplateVariables | null>(null);
  const [focusedInput, setFocusedInput] = useState<'subject' | 'body' | null>(null);
  const subjectInputRef = useRef<HTMLInputElement>(null);
  const subjectCursorRef = useRef<{ start: number; end: number }>({ start: 0, end: 0 });
  const bodyTextareaRef = useRef<HTMLTextAreaElement>(null);
  const bodyCursorRef = useRef<{ start: number; end: number }>({ start: 0, end: 0 });

  // Editor mode from URL params for proper breadcrumb support
  const editorMode = searchParams.get('mode') === 'visual' ? 'visual' : 'code';
  const setEditorMode = (mode: 'code' | 'visual') => {
    const newParams = new URLSearchParams(searchParams);
    if (mode === 'visual') {
      newParams.set('mode', 'visual');
    } else {
      newParams.delete('mode');
    }
    setSearchParams(newParams, { replace: true });
  };
  const [builderDocument, setBuilderDocument] = useState<EmailDocument | null>(null);

  const isNew = !initialTemplateId;

  // Fetch practice name
  useEffect(() => {
    const fetchPractice = async () => {
      if (!user?.practice_id) return;
      try {
        const token = localStorage.getItem('access_token');
        const response = await fetch(`${API_BASE_URL}/api/v1/practices/${user.practice_id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const practice = await response.json();
          setPracticeName(practice.name || 'Your Practice');
          const emailSlug = (practice.name || 'practice').toLowerCase().replace(/[^a-z0-9]/g, '');
          setPracticeEmail(`noreply@${emailSlug}.com`);
          setPracticeCity(practice.city || '');
          setPracticeState(practice.state || '');
          setPracticeSlug(practice.slug || '');
          setPracticeOnlineBookingEnabled(Boolean(practice.online_booking_enabled));
        }
      } catch (err) {
        console.error('Failed to fetch practice:', err);
      }
    };
    fetchPractice();
  }, [user?.practice_id]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      if (initialTemplateId) {
        const templates = await getTemplates();
        const found = templates.find(t => t.id === initialTemplateId);
        if (found) {
          setTemplate(found);
          setFormName(found.name);
          setFormChannel(found.channel);
          setFormSubject(found.subject || '');
          setFormBody(found.body);
          // Load builder document if it exists
          if (found.builder_document) {
            setBuilderDocument(found.builder_document as unknown as EmailDocument);
          }
          // Notify parent of template name
          onTemplateNameChange?.(found.name);
        }
      }
      
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load template');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [initialTemplateId]);

  // Fetch variables for the toolbar
  useEffect(() => {
    const fetchVariables = async () => {
      try {
        const varsData = await getTemplateVariables();
        setVariables(varsData);
      } catch (err) {
        console.error('Failed to fetch variables:', err);
      }
    };
    fetchVariables();
  }, []);

  // Insert variable at cursor position
  const insertVariable = (varName: string) => {
    const variable = `{{${varName}}} `; // Trailing space for convenience

    if (focusedInput === 'subject') {
      const { start, end } = subjectCursorRef.current;
      setFormSubject((prev) => {
        const before = prev.substring(0, start);
        const after = prev.substring(end);
        return before + variable + after;
      });

      setTimeout(() => {
        const input = subjectInputRef.current;
        if (input) {
          input.focus();
          const newCursorPos = start + variable.length;
          input.setSelectionRange(newCursorPos, newCursorPos);
          subjectCursorRef.current = { start: newCursorPos, end: newCursorPos };
        }
      }, 0);
    } else {
      // Insert into body
      const { start, end } = bodyCursorRef.current;
      setFormBody((prev) => {
        const before = prev.substring(0, start);
        const after = prev.substring(end);
        return before + variable + after;
      });

      setTimeout(() => {
        const textarea = bodyTextareaRef.current;
        if (textarea) {
          textarea.focus();
          const newCursorPos = start + variable.length;
          textarea.setSelectionRange(newCursorPos, newCursorPos);
          bodyCursorRef.current = { start: newCursorPos, end: newCursorPos };
        }
      }, 0);
    }
  };

  // Handle escape key - disabled in visual mode (HTML builder) to prevent accidental exit
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape' && editorMode !== 'visual') {
      onBack();
    }
  }, [onBack, editorMode]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormSubmitting(true);

    const data: TemplateCreateRequest = {
      name: formName,
      channel: formChannel,
      subject: formChannel !== 'sms' ? formSubject : undefined,
      body: formBody,
      // Include builder document if we have one (preserves visual builder state)
      builder_document: builderDocument ? (builderDocument as unknown as Record<string, unknown>) : undefined,
    };

    try {
      if (currentTemplateId) {
        await updateTemplate(currentTemplateId, data);
      } else {
        const newTemplate = await createTemplate(data);
        if (newTemplate?.id) {
          setCurrentTemplateId(newTemplate.id);
        }
      }
      onSave?.();
      onBack();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save template');
    } finally {
      setFormSubmitting(false);
    }
  };

  const getBookingLink = () => {
    if (!practiceOnlineBookingEnabled || !practiceCity || !practiceState || !practiceSlug) {
      return '';
    }
    const baseUrl = 'https://helppet.ai';
    const citySlug = practiceCity.trim().toLowerCase().replace(/\s+/g, '-');
    const stateSlug = practiceState.trim().toLowerCase();
    return `${baseUrl}/book/${stateSlug}/${citySlug}/${practiceSlug}`;
  };

  // Sample vaccine data for realistic preview
  const sampleVaccines = [
    { pet_name: 'Turbo', vaccine: 'Rabies', scheduled_date: 'Dec 20, 2025', days_overdue: '27' },
    { pet_name: 'Turbo', vaccine: 'Distemper', scheduled_date: 'Dec 15, 2025', days_overdue: '32' },
    { pet_name: 'Bella', vaccine: 'Bordetella', scheduled_date: 'Jan 2, 2026', days_overdue: '14' },
  ];

  const expandVaccinePreviewLoops = (body: string) => {
    const loopRegex = /\{\{#(overdue_vaccines|scheduled_vaccines|all_vaccines)\}\}([\s\S]*?)\{\{\/\1\}\}/g;
    return body.replace(loopRegex, (_match, listType: string, rowTemplate: string) => {
      const status = listType === 'scheduled_vaccines' ? 'Scheduled' : 'Overdue';
      
      // Generate multiple sample rows for realistic preview
      return sampleVaccines.map((vaccine, index) => {
        const bgColor = index % 2 === 0 ? '#ffffff' : '#f9fafb';
        return rowTemplate
          .replace(/\{\{#is_overdue\}\}/g, '')
          .replace(/\{\{\/is_overdue\}\}/g, '')
          .replace(/\{\{pet_name\}\}/gi, vaccine.pet_name)
          .replace(/\{\{vaccine_name\}\}/gi, vaccine.vaccine)
          .replace(/\{\{vaccine\}\}/gi, vaccine.vaccine)
          .replace(/\{\{scheduled_date\}\}/gi, vaccine.scheduled_date)
          .replace(/\{\{days_overdue\}\}/gi, vaccine.days_overdue)
          .replace(/\{\{status\}\}/g, status)
          .replace(/\{\{row_bg_color\}\}/g, bgColor);
      }).join('');
    });
  };

  const previewBody = (body: string) => {
    const bookingLink = getBookingLink() || 'https://helppet.ai/book/ca/anytown/happy-paws-vet';
    return expandVaccinePreviewLoops(body)
      .replace(/\{\{\s*pet_owner_name\s*\}\}/gi, 'Jane Doe')
      .replace(/\{\{\s*pet_owner_first_name\s*\}\}/gi, 'Jane')
      .replace(/\{\{\s*pet_owner_email\s*\}\}/gi, 'jane.doe@email.com')
      .replace(/\{\{\s*pet_owner_phone\s*\}\}/gi, '(555) 123-4567')
      .replace(/\{\{\s*owner_pet_name\s*\}\}/gi, 'Turbo')
      .replace(/\{\{\s*pet_name\s*\}\}/gi, 'Turbo')
      .replace(/\{\{\s*pet_names\s*\}\}/gi, 'Turbo and Bella')
      .replace(/\{\{\s*has_have\s*\}\}/gi, 'has')
      .replace(/\{\{\s*is_are\s*\}\}/gi, 'is')
      .replace(/\{\{\s*name\s*\}\}/gi, 'Jane Doe')
      .replace(/\{\{\s*first_name\s*\}\}/gi, 'Jane')
      .replace(/\{\{\s*email\s*\}\}/gi, 'jane.doe@email.com')
      .replace(/\{\{\s*phone\s*\}\}/gi, '(555) 123-4567')
      .replace(/\{\{\s*practice_name\s*\}\}/gi, practiceName)
      .replace(/\{\{\s*practice_phone\s*\}\}/gi, '(555) 987-6543')
      .replace(/\{\{\s*practice_email\s*\}\}/gi, 'info@practice.com')
      .replace(/\{\{\s*practice_address\s*\}\}/gi, '123 Veterinary Lane, Pet City, CA 90210')
      .replace(/\{\{\s*next_appointment_date\s*\}\}/gi, 'Friday, January 3rd at 2:00 PM')
      .replace(/\{\{\s*appointment_date\s*\}\}/gi, 'Friday, January 3rd at 2:00 PM')
      .replace(/\{\{\s*form_link\s*\}\}/gi, 'https://helppet.ai/forms/example')
      .replace(/\{\{\s*is\/are\s*\}\}/gi, 'is')
      .replace(/\{\{\s*book_appointment_link\s*\}\}/gi, bookingLink);
  };

  // Handle auto-save from visual builder (no navigation, no mode change)
  const handleBuilderSave = async (html: string, document: EmailDocument, subject?: string) => {
    // Update local state without changing modes
    setFormBody(html);
    setBuilderDocument(document);
    
    // Update subject if provided from builder
    if (subject) {
      setFormSubject(subject);
    }
    
    // Silently save the template with builder document (no navigation!)
    const data: TemplateCreateRequest = {
      name: formName || 'Untitled Template',
      channel: 'email_html',
      subject: subject || formSubject || 'Email from ' + practiceName,
      body: html,
      builder_document: document as unknown as Record<string, unknown>,
    };
    
    try {
      if (currentTemplateId) {
        // Update existing template
        await updateTemplate(currentTemplateId, data);
      } else {
        // For new templates, create and capture the ID for future saves
        const newTemplate = await createTemplate(data);
        if (newTemplate?.id) {
          setCurrentTemplateId(newTemplate.id);
          // Update URL so browser refresh works and future saves update instead of create
          setSearchParams({ tab: 'templates', templateId: newTemplate.id, mode: 'visual' });
        }
      }
      // Don't call onSave or onBack - this is auto-save, stay in builder!
    } catch (err) {
      console.error('Auto-save failed:', err);
      // Show error but don't navigate
      setError(err instanceof Error ? err.message : 'Auto-save failed');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  // Show visual builder if in visual mode and HTML channel
  if (editorMode === 'visual' && formChannel === 'email_html') {
    return (
      <EmailBuilder
        initialDocument={builderDocument || undefined}
        onSave={handleBuilderSave}
        practiceName={practiceName}
        bookingLink={getBookingLink() || undefined}
        initialSubject={formSubject}
        templateName={formName}
      />
    );
  }

  return (
    <div className="h-full flex flex-col bg-gray-100 dark:bg-dark-bg-primary relative">
      {error && (
        <div className="mx-6 mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm flex items-center gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Split Pane Layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Pane - Edit Form */}
        <div className="w-1/2 border-r border-gray-200 dark:border-dark-border overflow-y-auto bg-white dark:bg-dark-bg-secondary">
          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            {/* Template Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
                Template Name
              </label>
              <input
                type="text"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-lg dark:bg-dark-bg-tertiary dark:text-dark-text-primary focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g., Holiday Greeting, Appointment Reminder"
              />
            </div>

            {/* Channel Display - Read Only (type is locked after creation) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
                Message Type
              </label>
              <div className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg ${
                formChannel === 'sms'
                  ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800'
                  : 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-800'
              }`}>
                {formChannel === 'sms' && <MessageSquare className="w-4 h-4" />}
                {formChannel === 'email_plain' && <Mail className="w-4 h-4" />}
                {formChannel === 'email_html' && <Code className="w-4 h-4" />}
                <span className="text-sm font-medium">
                  {formChannel === 'sms' ? 'SMS' : formChannel === 'email_plain' ? 'Plain Email' : 'HTML Email'}
                </span>
              </div>
            </div>

            {/* Visual Builder Button - Only for HTML emails */}
            {formChannel === 'email_html' && (
              <div className={`border rounded-lg p-4 ${
                builderDocument 
                  ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                  : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      builderDocument 
                        ? 'bg-green-100 dark:bg-green-800/50'
                        : 'bg-blue-100 dark:bg-blue-800/50'
                    }`}>
                      <Layout className={`w-5 h-5 ${
                        builderDocument 
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-blue-600 dark:text-blue-400'
                      }`} />
                    </div>
                    <div>
                      <h4 className={`text-sm font-medium ${
                        builderDocument 
                          ? 'text-green-900 dark:text-green-300'
                          : 'text-blue-900 dark:text-blue-300'
                      }`}>
                        Visual Email Builder
                        {builderDocument && <span className="ml-2 text-xs font-normal">(has saved design)</span>}
                      </h4>
                      <p className={`text-xs ${
                        builderDocument 
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-blue-600 dark:text-blue-400'
                      }`}>
                        {builderDocument 
                          ? 'Continue editing your design'
                          : 'Drag-and-drop email design'
                        }
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setEditorMode('visual')}
                    className={`flex items-center gap-2 px-4 py-2 text-white text-sm font-medium rounded-lg transition-colors ${
                      builderDocument 
                        ? 'bg-green-600 hover:bg-green-700'
                        : 'bg-blue-600 hover:bg-blue-700'
                    }`}
                  >
                    <Wand2 className="w-4 h-4" />
                    {builderDocument ? 'Continue Editing' : 'Open Builder'}
                  </button>
                </div>
              </div>
            )}

            {/* Subject (for emails only) */}
            {formChannel !== 'sms' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
                  Email Subject
                </label>
                
                {/* Floating Variable Toolbar for Subject */}
                {focusedInput === 'subject' && variables && (
                  <div
                    className="mb-1"
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    <div className="inline-flex flex-wrap gap-1 bg-slate-800 rounded-lg shadow-xl p-2">
                      {Object.entries(variables.variables).map(([key, desc]) => (
                        <button
                          key={key}
                          type="button"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            insertVariable(key);
                          }}
                          className="px-2.5 py-1 text-xs text-slate-300 bg-slate-700 hover:bg-slate-600 rounded transition-colors"
                          title={`{{${key}}}`}
                        >
                          {desc}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                
                <input
                  ref={subjectInputRef}
                  type="text"
                  value={formSubject}
                  onChange={(e) => setFormSubject(e.target.value)}
                  onFocus={() => setFocusedInput('subject')}
                  onBlur={() => setFocusedInput(null)}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      setFocusedInput(null);
                    }
                  }}
                  onSelect={(e) => {
                    const target = e.target as HTMLInputElement;
                    subjectCursorRef.current = {
                      start: target.selectionStart || 0,
                      end: target.selectionEnd || 0,
                    };
                  }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-lg dark:bg-dark-bg-tertiary dark:text-dark-text-primary focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., Hello {{pet_owner_first_name}}, from {{practice_name}}"
                />
              </div>
            )}

            {/* Message Body */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
                Message Body
              </label>
              
              {/* Floating Variable Toolbar for Body - only show for non-HTML */}
              {formChannel !== 'email_html' && focusedInput === 'body' && variables && (
                <div
                  className="mb-1"
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <div className="inline-flex flex-wrap gap-1 bg-slate-800 rounded-lg shadow-xl p-2">
                    {Object.entries(variables.variables).map(([key, desc]) => (
                      <button
                        key={key}
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          insertVariable(key);
                        }}
                        className="px-2.5 py-1 text-xs text-slate-300 bg-slate-700 hover:bg-slate-600 rounded transition-colors"
                        title={`{{${key}}}`}
                      >
                        {desc}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              <textarea
                ref={bodyTextareaRef}
                value={formBody}
                onChange={(e) => setFormBody(e.target.value)}
                onFocus={() => setFocusedInput('body')}
                onBlur={() => setFocusedInput(null)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    setFocusedInput(null);
                  }
                }}
                onSelect={(e) => {
                  const target = e.target as HTMLTextAreaElement;
                  bodyCursorRef.current = {
                    start: target.selectionStart || 0,
                    end: target.selectionEnd || 0,
                  };
                }}
                required
                rows={formChannel === 'email_html' ? 16 : 10}
                className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-lg dark:bg-dark-bg-tertiary dark:text-dark-text-primary focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                placeholder={
                  formChannel === 'email_html'
                    ? 'Select a template above or write your HTML here...'
                    : 'Hi {{pet_owner_first_name}}, this is a message from {{practice_name}}...'
                }
              />
              {formChannel === 'sms' && (
                <p className="text-xs text-gray-500 dark:text-dark-text-muted mt-1">
                  {formBody.length}/160 characters
                  {formBody.length > 160 && (
                    <span className="text-amber-600 ml-1">
                      ({Math.ceil(formBody.length / 160)} segments)
                    </span>
                  )}
                </p>
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-dark-border">
              <button
                type="button"
                onClick={onBack}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-dark-text-primary bg-white dark:bg-dark-bg-secondary border border-gray-300 dark:border-dark-border rounded-lg hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={formSubmitting || !formName || !formBody}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                <Save className="w-4 h-4" />
                {formSubmitting ? 'Saving...' : isNew ? 'Create' : 'Save'}
              </button>
            </div>
          </form>
        </div>

        {/* Right Pane - Live Preview */}
        <div className="w-1/2 overflow-y-auto bg-gray-50 dark:bg-dark-bg-primary">
          {/* Preview Header */}
          <div className="sticky top-0 bg-gray-50 dark:bg-dark-bg-primary border-b border-gray-200 dark:border-dark-border px-6 py-3 z-10">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-dark-text-secondary">
                <Eye className="w-4 h-4" />
                Live Preview
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-dark-text-muted">
                {formChannel === 'sms' ? (
                  <>
                    <Smartphone className="w-4 h-4" />
                    SMS Preview
                  </>
                ) : (
                  <>
                    <Monitor className="w-4 h-4" />
                    Email Preview
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Preview Content */}
          <div className="p-6">
            {formChannel === 'sms' ? (
              /* SMS Preview - Phone mockup */
              <div className="max-w-xs mx-auto">
                <div className="bg-gray-900 rounded-[2.5rem] p-3 shadow-xl">
                  <div className="bg-white rounded-[2rem] overflow-hidden">
                    <div className="bg-gray-100 px-6 py-2 flex justify-between items-center text-xs text-gray-500">
                      <span>9:41</span>
                      <div className="w-20 h-5 bg-black rounded-full"></div>
                      <span>100%</span>
                    </div>
                    <div className="px-4 py-3 border-b border-gray-200 text-center">
                      <div className="text-sm font-medium text-gray-900">
                        {formName || 'New Message'}
                      </div>
                      <div className="text-xs text-gray-500">Text Message</div>
                    </div>
                    <div className="p-4 min-h-[200px] bg-gray-50">
                      {formBody ? (
                        <div className="bg-green-500 text-white rounded-2xl rounded-br-md px-4 py-2 max-w-[85%] ml-auto shadow-sm">
                          <p className="text-sm whitespace-pre-wrap">
                            {previewBody(formBody)}
                          </p>
                        </div>
                      ) : (
                        <div className="text-center text-gray-400 text-sm py-8">
                          Start typing to see preview
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ) : formChannel === 'email_html' ? (
              /* HTML Email Preview - iPhone Mockup */
              <div className="max-w-xs mx-auto">
                <div className="bg-gray-900 rounded-[2.5rem] p-3 shadow-xl">
                  <div className="bg-white rounded-[2rem] overflow-hidden">
                    {/* iPhone Status Bar */}
                    <div className="bg-gray-100 px-6 py-2 flex justify-between items-center text-xs text-gray-500">
                      <span>9:41</span>
                      <div className="w-20 h-5 bg-black rounded-full"></div>
                      <div className="flex items-center gap-1">
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M2 17h2v4H2v-4zm4-5h2v9H6v-9zm4-4h2v13h-2V8zm4-4h2v17h-2V4z"/>
                        </svg>
                        <svg className="w-5 h-3" viewBox="0 0 25 12" fill="none">
                          <rect x="0.5" y="0.5" width="21" height="11" rx="2.5" stroke="currentColor"/>
                          <rect x="2" y="2" width="18" height="8" rx="1" fill="#34C759"/>
                          <path d="M23 4v4a2 2 0 002-2v0a2 2 0 00-2-2z" fill="currentColor"/>
                        </svg>
                      </div>
                    </div>
                    
                    {/* Spacer for taller phone */}
                    <div className="h-8 bg-white"></div>
                    
                    {/* Gmail-style Header */}
                    <div className="px-4 py-2 flex items-center justify-between border-b border-gray-200">
                      <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                      </svg>
                      <div className="flex items-center gap-3">
                        <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                        </svg>
                        <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                        </svg>
                      </div>
                    </div>
                    
                    {/* Subject Line */}
                    <div className="px-4 py-3 border-b border-gray-100 overflow-hidden">
                      <div className="flex items-center gap-2">
                        <h3 className="text-base font-medium text-gray-900 flex-1 truncate">
                          {formSubject ? previewBody(formSubject) : '(No subject)'}
                        </h3>
                        <span className="px-2 py-0.5 bg-gray-200 text-gray-600 text-xs rounded">Inbox</span>
                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                        </svg>
                      </div>
                    </div>
                    
                    {/* Sender Info */}
                    <div className="px-4 py-3 flex items-start gap-3 border-b border-gray-100">
                      <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-medium text-sm">
                        {practiceName.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900 text-sm">{practiceName}</span>
                          <span className="text-gray-400 text-xs">Just now</span>
                        </div>
                        <p className="text-xs text-gray-500 text-left">to me</p>
                      </div>
                    </div>
                    
                    {/* Email Body */}
                    <div style={{ minHeight: '300px', maxHeight: '400px', overflowY: 'auto', overflowX: 'hidden' }} className="p-4 bg-white">
                      {formBody ? (
                        <div 
                          className="text-left text-sm pointer-events-none [&_a]:no-underline [&_a]:text-inherit break-words overflow-hidden"
                          style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
                          dangerouslySetInnerHTML={{ __html: previewBody(formBody) }}
                        />
                      ) : (
                        <div className="text-center text-gray-400 text-sm py-12">
                          Start typing to see preview
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              /* Plain Email Preview - Desktop style */
              <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow-sm border border-gray-200 dark:border-dark-border overflow-hidden">
                <div className="bg-gray-50 dark:bg-dark-bg-tertiary px-6 py-4 border-b border-gray-200 dark:border-dark-border">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-gray-500 dark:text-dark-text-muted w-16">From:</span>
                      <span className="text-gray-900 dark:text-dark-text-primary">
                        {practiceName} &lt;{practiceEmail}&gt;
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-gray-500 dark:text-dark-text-muted w-16">To:</span>
                      <span className="text-gray-900 dark:text-dark-text-primary">
                        Jane Doe &lt;jane.doe@email.com&gt;
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-gray-500 dark:text-dark-text-muted w-16">Subject:</span>
                      <span className="text-gray-900 dark:text-dark-text-primary font-medium">
                        {formSubject ? previewBody(formSubject) : '(No subject)'}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="p-6 min-h-[300px]">
                  {formBody ? (
                    <p className="text-gray-700 dark:text-dark-text-primary whitespace-pre-wrap text-sm leading-relaxed">
                      {previewBody(formBody)}
                    </p>
                  ) : (
                    <div className="text-center text-gray-400 text-sm py-12">
                      Start typing to see preview
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="mt-4 text-center">
              <p className="text-xs text-gray-500 dark:text-dark-text-muted">
                Variables are replaced with sample data for preview
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateEditView;
